import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box } from '@mui/material';
import { useSetRecoilState } from 'recoil';
import { Order } from '../../interfaces/Order/Order';
import { getShippingProviders } from '../../api/ShippingProvider';
import { getOrdersByIDs, getShippingBarcode } from '../../api/Order';
import { Waiting } from '../../components/Waiting/Waiting';
import { getSettingsByType } from '../../api/Settings';
import { PrintShipmentPaper } from './PrintShipmentPaper';
import { shippingProvidersAtom } from '../../atoms/ShippingProvider';

export const PrintShipmentPage = () => {
  const { orderIDs: orderIDsAsStr } = useParams<string>();

  const orderIDs = orderIDsAsStr?.split(',').map(Number) ?? [];

  const setShippingProviders = useSetRecoilState(shippingProvidersAtom);

  const [orders, setOrders] = useState<Order[]>([]);
  const [barcodeImages, setBarcodeImages] = useState<(string | undefined)[]>(
    [],
  );
  const [storeName, setStoreName] = useState<string | undefined>(undefined);

  const [loadingShippingProviders, setLoadingShippingProviders] =
    useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingBarcodes, setLoadingBarcodes] = useState(false);
  const [loadingName, setLoadingName] = useState(false);

  const loading =
    loadingShippingProviders || loadingOrders || loadingBarcodes || loadingName;

  useEffect(() => {
    setLoadingShippingProviders(true);
    getShippingProviders()
      .then((s) => setShippingProviders(s))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingShippingProviders(false));

    setLoadingOrders(true);
    getOrdersByIDs(orderIDs)
      .then((res) => setOrders(res.data))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingOrders(false));

    setLoadingBarcodes(true);
    Promise.all(orderIDs.map((orderID) => getShippingBarcode(orderID)))
      .then((b) => setBarcodeImages(b))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingBarcodes(false));

    setLoadingName(true);
    getSettingsByType('store')
      .then((s) =>
        setStoreName(
          s.find((x) => x.key === 'store_name')?.genericValue?.stringValue ??
            '',
        ),
      )
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingName(false));
  }, []);

  useEffect(() => {
    if (loading) {
      return () => {};
    }

    const t = setTimeout(() => window.print(), 500);

    return () => {
      clearTimeout(t);
    };
  }, [loading]);

  if (loading) {
    return <Waiting open />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 800,
        alignItems: 'center',
        backgroundColor: 'white',
        minHeight: '100vh',
      }}
    >
      {orders.map((order, i) => (
        <PrintShipmentPaper
          key={i}
          storeName={storeName ?? ''}
          barcodeImage={barcodeImages[i]}
          order={order}
          breakPage={i < orders.length - 1}
        />
      ))}
    </Box>
  );
};
